import React, { useMemo } from "react";
import {
  Badge,
  type BadgeProps,
  Box,
  ColorSwatch,
  Icon,
  type IconName,
  isIconName,
  toColor,
} from "@salestrak/ui";
import { AGE_STATUS, AGE_STATUS_CONFIG } from "#app/lib/age-status";
import {
  normalizeOutcomeTone,
  OUTCOME_TONE_CONFIG,
} from "#app/lib/outcome-tone";
import type {
  ActivityAgeSnapshot,
  ActivityStageInfo,
  ActivityWorkflowInfo,
} from "./activity-types";
import { NoteThread } from "./note-thread";
import type { Omittable } from "@salestrak/ui";

type StageChange = Omittable<{
  oldStage?: ActivityStageInfo | null;
  newStage?: ActivityStageInfo | null;
  oldWorkflow?: ActivityWorkflowInfo | null;
  newWorkflow?: ActivityWorkflowInfo | null;
}>;

type AgeItemProps = Omittable<{
  issueId: string;
  noteId: string;
  ageChangeset: {
    before?: ActivityAgeSnapshot | null;
    after?: ActivityAgeSnapshot | null;
  };
  reason?: { name?: string } | null;
  /** Stage move recorded in the same visit (rendered as a chip). */
  stageChange?: StageChange | null;
  /** The visit (re)assigned the current user as primary. */
  assignedToSelf?: boolean;
}>;

/** The glyph an age post wears when its writer named none — a project's own
 *  visit is a clock reading, and the ladder is the vocabulary it reads by. */
const DEFAULT_STATUS_ICON: IconName = "clock";

export function AgeItem({
  issueId,
  noteId,
  ageChangeset,
  reason,
  stageChange,
  assignedToSelf,
}: AgeItemProps) {
  const statusBadge = useMemo(() => {
    const after = ageChangeset?.after;
    const status = after?.status;
    if (!status) return null;
    // Posting an Update IS what puts the clock back on track, so every post
    // lands On track whatever happened at the door: the badge would only
    // repeat itself, and beside a "Denied" outcome it read as a verdict on
    // the visit. The outcome (and its tone) is the result; the clock word
    // shows only when a snapshotted vocabulary named its own.
    if (status === AGE_STATUS.onTrack && !after?.statusLabel) return null;

    // `background` is the semantic hue name a Badge takes directly; the label
    // is the shared vocabulary. `disabled` has no entry — the timeline records
    // posted Updates, and a Project with updates off posts none.
    const config: Record<
      string,
      { label: string; color: BadgeProps["color"] }
    > = {
      [AGE_STATUS.onTrack]: {
        label: AGE_STATUS_CONFIG.onTrack.label,
        color: AGE_STATUS_CONFIG.onTrack.background,
      },
      [AGE_STATUS.atRisk]: {
        label: AGE_STATUS_CONFIG.atRisk.label,
        color: AGE_STATUS_CONFIG.atRisk.background,
      },
      [AGE_STATUS.offTrack]: {
        label: AGE_STATUS_CONFIG.offTrack.label,
        color: AGE_STATUS_CONFIG.offTrack.background,
      },
    };

    const entry = config[status];
    // A snapshotted label carries the entry on its own: the retired
    // vocabularies keyed buckets this ladder never had (`disabled` among
    // them), and the word they showed is the whole point of storing it.
    const label = after?.statusLabel ?? entry?.label;
    if (!label) return null;
    return {
      label,
      color: toColor(after?.statusColor) ?? entry?.color,
      icon: isIconName(after?.statusIcon)
        ? after.statusIcon
        : DEFAULT_STATUS_ICON,
    };
  }, [ageChangeset]);

  const reasonBadge = useMemo(() => {
    const tone = ageChangeset?.after?.reasonTone;
    if (reason?.name)
      return {
        label: reason.name,
        // A tone rides only where the writer resolved one. It stays absent for
        // an outcome the live pool no longer knows, and for the "Every N days"
        // window that borrows this slot on a follow-up-started entry — a
        // window is not an outcome and wears no dot.
        tone: tone != null ? normalizeOutcomeTone(tone) : null,
      };
    if (ageChangeset?.after?.reasonId)
      return { label: "Reason deleted", tone: null };
    return null;
  }, [reason, ageChangeset]);

  // Stage move chip — "Old → New" (workflow-prefixed only when it changed too).
  const stageLabel = useMemo(() => {
    const oldStage = stageChange?.oldStage;
    const newStage = stageChange?.newStage;
    if (!newStage) return null;
    const oldWf = stageChange?.oldWorkflow;
    const newWf = stageChange?.newWorkflow;
    const crossWorkflow = !!(oldWf && newWf && oldWf.workflowId !== newWf.workflowId);
    const newLabel =
      crossWorkflow && newWf ? `${newWf.name} › ${newStage.name}` : newStage.name;
    if (oldStage) {
      const oldLabel =
        crossWorkflow && oldWf ? `${oldWf.name} › ${oldStage.name}` : oldStage.name;
      if (oldLabel !== newLabel) return `${oldLabel} → ${newLabel}`;
    }
    return newLabel;
  }, [stageChange]);

  const hasBadges = statusBadge || reasonBadge || stageLabel || assignedToSelf;
  const badges = hasBadges ? (
    <Box direction="row" gap="xs" wrap>
      {statusBadge && (
        <Badge color={statusBadge.color}>
          <Icon name={statusBadge.icon} />
          {statusBadge.label}
        </Badge>
      )}
      {reasonBadge && (
        <Badge tone="outline">
          {/* Neutral paints no dot: most outcomes are neutral, and a feed of
              grey dots says nothing. */}
          {reasonBadge.tone && reasonBadge.tone !== "neutral" ? (
            <ColorSwatch
              color={OUTCOME_TONE_CONFIG[reasonBadge.tone].swatch}
              rounded="full"
              size="xs"
            />
          ) : null}
          {reasonBadge.label}
        </Badge>
      )}
      {stageLabel && (
        <Badge color="info">
          <Icon name="git-branch" />
          {stageLabel}
        </Badge>
      )}
      {assignedToSelf && (
        <Badge color="success">
          <Icon name="user" />
          Assigned to self
        </Badge>
      )}
    </Box>
  ) : undefined;

  return <NoteThread issueId={issueId} noteId={noteId} headerExtra={badges} />;
}
